import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Download, Percent, Info } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { useRevenue, deptLabel, type SaleRow } from "./api";

// ============================================================================
// Margin & profitability — gross margin from mirror_sales: revenue (inv_amount)
// minus ESC's material/labor/other cost split, by period / department /
// salesman / zone. The ESC invoice bottom-right box, made a roll-up report.
// INTERNAL ONLY — never customer-facing. ESC stays the accounting system of
// record; this reads the mirror.
// ============================================================================

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayISO = () => iso(new Date());
const yearsAgoISO = (n: number) => { const d = new Date(); d.setFullYear(d.getFullYear() - n); return iso(d); };

type PresetKey = "this-year" | "last-year" | "last-12mo" | "last-3yr" | "custom";
function presetRange(key: PresetKey): { from: string; to: string } {
  const y = new Date().getFullYear();
  switch (key) {
    case "this-year": return { from: `${y}-01-01`, to: todayISO() };
    case "last-year": return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case "last-12mo": return { from: yearsAgoISO(1), to: todayISO() };
    case "last-3yr": return { from: yearsAgoISO(3), to: todayISO() };
    default: return { from: `${y}-01-01`, to: todayISO() };
  }
}
const PRESETS: [PresetKey, string][] = [
  ["this-year", "This year"], ["last-year", "Last year"],
  ["last-12mo", "Last 12 months"], ["last-3yr", "Last 3 years"], ["custom", "Custom range"],
];

type GroupKey = "dept" | "salesman" | "month" | "zone";
const GROUPS: [GroupKey, string][] = [
  ["dept", "Department"], ["salesman", "Salesman"], ["month", "Month"], ["zone", "Zone (city)"],
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (ym: string) => { const [y, m] = ym.split("-"); return `${MONTHS[parseInt(m, 10) - 1]} ${y}`; };
const money = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const pctStr = (v: number) => `${v.toFixed(1)}%`;
const costOf = (r: SaleRow) => r.mat_cost + r.labor_cost + r.other_cost;
const marginColor = (m: number) =>
  m >= 45 ? "text-emerald-600" : m >= 30 ? "text-slate-700" : m >= 15 ? "text-amber-600" : "text-red-600";

function groupValue(r: SaleRow, by: GroupKey): string {
  switch (by) {
    case "month": return r.inv_date.slice(0, 7);
    case "dept": return deptLabel(r.dept);
    case "salesman": return (r.salesman ?? "").trim() || "(unassigned)";
    case "zone": return (r.city ?? "").trim() || "(unknown zone)";
  }
}

interface GroupRow { key: string; label: string; revenue: number; cost: number; count: number }

function StyledSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="h-9 cursor-pointer appearance-none rounded-lg border border-hairline bg-white pl-3 pr-9 text-sm font-medium text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30">
        {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

export function MarginReport() {
  const [preset, setPreset] = useState<PresetKey>("this-year");
  const [from, setFrom] = useState(() => presetRange("this-year").from);
  const [to, setTo] = useState(() => presetRange("this-year").to);
  const [groupBy, setGroupBy] = useState<GroupKey>("dept");
  const { data, isLoading, isError } = useRevenue(from, to);

  function choosePreset(key: PresetKey) {
    setPreset(key);
    if (key !== "custom") { const r = presetRange(key); setFrom(r.from); setTo(r.to); }
  }

  const { groups, totalRev, totalCost, totalCount } = useMemo(() => {
    const all = data?.rows ?? [];
    const map = new Map<string, GroupRow>();
    let totalRev = 0, totalCost = 0;
    for (const r of all) {
      const key = groupValue(r, groupBy);
      const g = map.get(key) ?? { key, label: groupBy === "month" ? monthLabel(key) : key, revenue: 0, cost: 0, count: 0 };
      g.revenue += r.inv_amount; g.cost += costOf(r); g.count += 1;
      map.set(key, g);
      totalRev += r.inv_amount; totalCost += costOf(r);
    }
    let groups = Array.from(map.values());
    groups = groupBy === "month"
      ? groups.sort((a, b) => a.key.localeCompare(b.key))
      : groups.sort((a, b) => (b.revenue - b.cost) - (a.revenue - a.cost));
    return { groups, totalRev, totalCost, totalCount: all.length };
  }, [data, groupBy]);

  const totalGP = totalRev - totalCost;
  const blendedMargin = totalRev > 0 ? (totalGP / totalRev) * 100 : 0;
  const maxGP = groups.reduce((m, g) => Math.max(m, g.revenue - g.cost), 0);
  const TOP = 15;
  const chartGroups = groupBy === "month" ? groups : groups.slice(0, TOP);
  const groupLabel = GROUPS.find(([k]) => k === groupBy)?.[1] ?? groupBy;

  function exportCsv() {
    const lines: string[] = [];
    lines.push("Sedgwick Heating — Margin & profitability (INTERNAL)");
    lines.push(`Date range,${from} to ${to}`);
    lines.push(`Grouped by,${groupLabel}`);
    lines.push(`Total revenue,${totalRev.toFixed(2)}`);
    lines.push(`Total cost,${totalCost.toFixed(2)}`);
    lines.push(`Gross profit,${totalGP.toFixed(2)}`);
    lines.push(`Blended margin,${blendedMargin.toFixed(1)}%`);
    if (data?.truncated) lines.push("Note,Results capped — totals are a floor; narrow the range");
    lines.push("");
    lines.push(`${groupLabel},Revenue,Cost,Gross profit,Margin %,Invoices`);
    for (const g of groups) {
      const gp = g.revenue - g.cost; const m = g.revenue > 0 ? (gp / g.revenue) * 100 : 0;
      const safe = /[",\n]/.test(g.label) ? `"${g.label.replace(/"/g, '""')}"` : g.label;
      lines.push(`${safe},${g.revenue.toFixed(2)},${g.cost.toFixed(2)},${gp.toFixed(2)},${m.toFixed(1)}%,${g.count}`);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `margin_${groupBy}_${from}_${to}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  return (
    <div>
      <Card className="mb-5 p-4">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <Field label="Date range"><StyledSelect value={preset} onChange={(v) => choosePreset(v as PresetKey)} options={PRESETS} /></Field>
          <Field label="From"><input type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} className="h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" /></Field>
          <Field label="To"><input type="date" value={to} min={from} max={todayISO()} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} className="h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" /></Field>
          <Field label="Group by"><StyledSelect value={groupBy} onChange={(v) => setGroupBy(v as GroupKey)} options={GROUPS} /></Field>
          <div className="ml-auto"><Button variant="secondary" size="sm" onClick={exportCsv} disabled={groups.length === 0}><Download className="h-4 w-4" /> Export CSV</Button></div>
        </div>
      </Card>

      {!isLoading && !isError && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Revenue" value={money(totalRev)} />
          <Stat label="Cost" value={money(totalCost)} />
          <Stat label="Gross profit" value={money(totalGP)} accent
            info="Invoice revenue minus ESC's material, labor and other cost, by invoice date; blended margin is gross profit as a share of revenue. Internal only, never shown to customers. ESC stays the accounting system of record; this is a read-only view." />
          <Stat label="Blended margin" value={pctStr(blendedMargin)} accent />
        </div>
      )}
      {data?.truncated && <p className="mb-3 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700"><Info className="h-3.5 w-3.5" /> Results capped — narrow the range</p>}

      {isError ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-600">Couldn't load margin. Check the connection and try again.</p></Card>
      ) : isLoading ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-500">Loading margin…</p></Card>
      ) : groups.length === 0 ? (
        <Card className="p-10 text-center"><Percent className="mx-auto h-10 w-10 text-slate-300" /><h3 className="mt-3 text-[15px] font-bold tracking-tight text-slate-900">No sales in this range</h3></Card>
      ) : (
        <>
          <Card className="mb-4 p-5">
            <h3 className="mb-4 text-[15px] font-bold tracking-tight text-slate-900">Gross Profit By {groupLabel.toLowerCase()}{chartGroups.length < groups.length && <span className="font-normal text-slate-400"> · top {TOP}</span>}</h3>
            <div className="space-y-2.5">
              {chartGroups.map((g, i) => {
                const gp = g.revenue - g.cost; const m = g.revenue > 0 ? (gp / g.revenue) * 100 : 0;
                return (
                  <div key={g.key} className="flex items-center gap-3">
                    <div className="w-40 flex-shrink-0 truncate text-right text-xs font-medium text-slate-600" title={g.label}>{g.label}</div>
                    <div className="relative h-6 flex-1 overflow-hidden rounded-md bg-slate-100">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${maxGP > 0 ? (gp / maxGP) * 100 : 0}%` }} transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: Math.min(i * 0.02, 0.25) }} className="h-full rounded-md bg-emerald-500" />
                    </div>
                    <div className="w-32 flex-shrink-0 text-right text-xs tabular-nums">
                      <span className="font-semibold text-slate-900">{money(gp)}</span>{" "}
                      <span className={marginColor(m)}>{pctStr(m)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">{groupLabel}</th>
                  <th className="px-4 py-2.5 text-right">Revenue</th>
                  <th className="px-4 py-2.5 text-right">Cost</th>
                  <th className="px-4 py-2.5 text-right">Gross profit</th>
                  <th className="px-4 py-2.5 text-right">Margin %</th>
                  <th className="px-4 py-2.5 text-right">Invoices</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups.map((g) => {
                  const gp = g.revenue - g.cost; const m = g.revenue > 0 ? (gp / g.revenue) * 100 : 0;
                  return (
                    <tr key={g.key} className="transition-colors hover:bg-emerald-50/40">
                      <td className="px-4 py-2.5 font-medium text-slate-800">{g.label}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{money(g.revenue)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{money(g.cost)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-900">{money(gp)}</td>
                      <td className={cn("px-4 py-2.5 text-right tabular-nums font-semibold", marginColor(m))}>{pctStr(m)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{g.count.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-hairline bg-slate-50/60 font-semibold">
                  <td className="px-4 py-2.5 text-slate-900">Total</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{money(totalRev)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{money(totalCost)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{money(totalGP)}</td>
                  <td className={cn("px-4 py-2.5 text-right tabular-nums", marginColor(blendedMargin))}>{pctStr(blendedMargin)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{totalCount.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent, info }: { label: string; value: string; accent?: boolean; info?: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-hairline bg-white px-4 py-3">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
        {info && <InfoTip className="-my-1 normal-case" label="How margin is figured">{info}</InfoTip>}
      </div>
      <div className={cn("mt-0.5 text-xl font-bold tabular-nums", accent ? "text-emerald-600" : "text-slate-900")}>{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>{children}</label>;
}
